import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private errorTimeout: any;
  private successTimeout: any;
  private warningTimeout: any;

  constructor(private translateService: TranslateService) {}

  // All toasts share one screen position, so only one type may be visible at a time
  private hideOthers(keep: 'error' | 'success' | 'warning') {
    const types = ['error', 'success', 'warning'] as const;
    types.filter((t) => t !== keep).forEach((t) => {
      document
        .querySelectorAll('.' + t + '_toaster_show')
        .forEach((el) => el.setAttribute('class', t + '_toaster'));
      const msg = document.getElementById(t + 'Msg');
      if (msg) {
        msg.innerHTML = '';
      }
      const key = (t + 'Timeout') as 'errorTimeout' | 'successTimeout' | 'warningTimeout';
      if (this[key]) {
        clearTimeout(this[key]);
        this[key] = null;
      }
    });
  }

  error(error: string) {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }

    if (error) {
      this.hideOthers('error');
      const formattedError = this.sanitizeErrorMessage(error);
      const translated = this.translateService?.instant
        ? this.translateService.instant(formattedError)
        : formattedError;
      const displayMessage = translated || formattedError;

      const errorElement = document.getElementById('errorMsg');
      if (errorElement) {
        errorElement.innerHTML = displayMessage;
      }

      const toasters = document.querySelectorAll(
        '.error_toaster, .error_toaster_show'
      );
      if (toasters.length > 0) {
        toasters[0].setAttribute('class', 'error_toaster_show');
        this.errorTimeout = setTimeout(() => {
          if (errorElement) {
            errorElement.innerHTML = '';
          }
          if (toasters.length > 0) {
            toasters[0].setAttribute('class', 'error_toaster');
          }
        }, 5000);
      }
    } else {
      const errorElement = document.getElementById('errorMsg');
      if (errorElement) {
        errorElement.innerHTML = '';
      }
      const showToasters = document.querySelectorAll('.error_toaster_show');
      showToasters.forEach((toaster) =>
        toaster.setAttribute('class', 'error_toaster')
      );
    }
  }

  success(message: string) {
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
      this.successTimeout = null;
    }

    if (message) {
      this.hideOthers('success');
      const translated = this.translateService?.instant
        ? this.translateService.instant(message)
        : message;
      const displayMessage = translated || message;

      const successElement = document.getElementById('successMsg');
      if (successElement) {
        successElement.innerHTML = displayMessage;
      }

      const toasters = document.querySelectorAll(
        '.success_toaster, .success_toaster_show'
      );
      if (toasters.length > 0) {
        toasters[0].setAttribute('class', 'success_toaster_show');
        this.successTimeout = setTimeout(() => {
          if (successElement) {
            successElement.innerHTML = '';
          }
          if (toasters.length > 0) {
            toasters[0].setAttribute('class', 'success_toaster');
          }
        }, 5000);
      }
    } else {
      const successElement = document.getElementById('successMsg');
      if (successElement) {
        successElement.innerHTML = '';
      }
      const showToasters = document.querySelectorAll('.success_toaster_show');
      showToasters.forEach((toaster) =>
        toaster.setAttribute('class', 'success_toaster')
      );
    }
  }

  warning(message: string) {
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }

    if (message) {
      this.hideOthers('warning');
      const translated = this.translateService?.instant
        ? this.translateService.instant(message)
        : message;
      const displayMessage = translated || message;

      const warningElement = document.getElementById('warningMsg');
      if (warningElement) {
        warningElement.innerHTML = displayMessage;
      }

      const toasters = document.querySelectorAll(
        '.warning_toaster, .warning_toaster_show'
      );
      if (toasters.length > 0) {
        toasters[0].setAttribute('class', 'warning_toaster_show');
        this.warningTimeout = setTimeout(() => {
          if (warningElement) {
            warningElement.innerHTML = '';
          }
          if (toasters.length > 0) {
            toasters[0].setAttribute('class', 'warning_toaster');
          }
        }, 5000);
      }
    } else {
      const warningElement = document.getElementById('warningMsg');
      if (warningElement) {
        warningElement.innerHTML = '';
      }
      const showToasters = document.querySelectorAll('.warning_toaster_show');
      showToasters.forEach((toaster) =>
        toaster.setAttribute('class', 'warning_toaster')
      );
    }
  }

  private sanitizeErrorMessage(error: string): string {
    if (typeof error !== 'string') return error;

    const lower = error.toLowerCase();
    const isOffline =
      (typeof navigator !== 'undefined' && !navigator.onLine) ||
      lower.includes('net::err_internet_disconnected') ||
      lower.includes('net::err_name_not_resolved');

    if (isOffline) {
      const isArabic =
        localStorage.getItem('language')?.toLowerCase().startsWith('ar') ||
        false;
      return isArabic
        ? 'لا يوجد اتصال بالإنترنت. يرجى التحقق من اتصالك بالشبكة والمحاولة مرة أخرى.'
        : 'No internet connection. Please check your network connection and try again.';
    }

    return error;
  }
}